/**
 * OIMS v2 - Profile module
 * Profile dropdown with Aurora glass design, Telegram account linking
 */

import { decodeJwt } from './auth.js';

let profileMenu, btnProfile, profileDropdown;
let profileDisplayName, profileEmail, telegramSection, telegramLinkStatus;
let countdownTimer = null;

const TELEGRAM_ELIGIBLE_GROUPS = ['admin', 'product_admin', 'project_admin', 'dev_admin'];

export function initProfile(state) {
  profileMenu = document.getElementById('profileMenu');
  btnProfile = document.getElementById('btnProfile');
  profileDropdown = document.getElementById('profileDropdown');
  profileDisplayName = document.getElementById('profileDisplayName');
  profileEmail = document.getElementById('profileEmail');
  telegramSection = document.getElementById('telegramSection');
  telegramLinkStatus = document.getElementById('telegramLinkStatus');

  if (!btnProfile || !profileDropdown) return;

  btnProfile.addEventListener('click', (e) => {
    e.stopPropagation();
    const opening = !profileDropdown.classList.contains('show');

    // Close notification dropdown if open
    const notifDropdown = document.getElementById('notifDropdown');
    if (notifDropdown) notifDropdown.classList.remove('show');

    profileDropdown.classList.toggle('show');

    if (opening && telegramSection && telegramSection.style.display !== 'none') {
      loadTelegramStatus();
    }
  });

  document.addEventListener('click', (e) => {
    if (profileDropdown && profileMenu && !profileMenu.contains(e.target)) {
      profileDropdown.classList.remove('show');
    }
  });

  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && profileDropdown.classList.contains('show')) {
      profileDropdown.classList.remove('show');
    }
  });
}

export function updateProfileInfo(state) {
  if (!state.accessToken) return;

  const decoded = decodeJwt(state.accessToken);
  if (!decoded) return;

  const email = decoded.email || decoded.username || decoded['cognito:username'] || '';
  // Try id_token-derived name first (stored at login), then JWT claims, then email fallback
  const storedName = localStorage.getItem('oims_display_name');
  const name = storedName || decoded.name;
  const givenName = decoded.given_name;
  const familyName = decoded.family_name;
  let displayName;
  if (name) {
    displayName = name;
  } else if (givenName) {
    displayName = familyName ? `${givenName} ${familyName}` : givenName;
  } else {
    displayName = email.includes('@') ? email.split('@')[0] : (email || 'User');
  }
  const groups = decoded['cognito:groups'] || [];

  if (profileDisplayName) profileDisplayName.textContent = displayName;
  if (profileEmail) profileEmail.textContent = email;

  if (telegramSection) {
    const eligible = groups.some(g => TELEGRAM_ELIGIBLE_GROUPS.includes(g));
    telegramSection.style.display = eligible ? 'block' : 'none';
  }
}

async function loadTelegramStatus() {
  if (!telegramLinkStatus) return;
  telegramLinkStatus.innerHTML = '<span style="font-size:0.75rem;color:var(--text-disabled)">Checking...</span>';

  try {
    const { data } = await axios.get('/telegram/link-status');
    if (data.linked) {
      renderLinkedStatus(data);
    } else {
      renderUnlinkedStatus();
    }
  } catch {
    telegramLinkStatus.innerHTML = '';
  }
}

function renderLinkedStatus(data) {
  const name = data.telegram_username
    ? `@${data.telegram_username}`
    : data.telegram_name || 'Linked';

  telegramLinkStatus.innerHTML =
    '<div class="telegram-linked">' +
      '<span class="telegram-linked-info">' +
        '<i class="bi bi-check-circle-fill"></i> ' + esc(name) +
      '</span>' +
      '<button class="btn-unlink-telegram" id="btnUnlinkTelegram" title="Unlink">' +
        '<i class="bi bi-x-lg"></i>' +
      '</button>' +
    '</div>';

  document.getElementById('btnUnlinkTelegram')?.addEventListener('click', unlinkTelegram);
}

function renderUnlinkedStatus() {
  telegramLinkStatus.innerHTML =
    '<button class="btn-link-telegram" id="btnGenerateLinkCode">' +
      '<i class="bi bi-link-45deg"></i> Generate Link Code' +
    '</button>';

  document.getElementById('btnGenerateLinkCode')?.addEventListener('click', generateLinkCode);
}

async function generateLinkCode() {
  const btn = document.getElementById('btnGenerateLinkCode');
  if (btn) {
    btn.disabled = true;
    btn.innerHTML = '<i class="bi bi-hourglass-split spin"></i> Generating...';
  }

  try {
    const { data } = await axios.post('/telegram/link-code');
    renderLinkCode(data.code, data.expires_in);
  } catch (e) {
    const msg = e.response?.data?.detail || 'Failed to generate code';
    if (window.toast) window.toast.error(msg);
    renderUnlinkedStatus();
  }
}

function renderLinkCode(code, expiresIn) {
  const expiryTime = Date.now() + (expiresIn * 1000);

  telegramLinkStatus.innerHTML =
    '<div class="link-code-display">' +
      '<div class="link-code-value">' + esc(code) + '</div>' +
      '<div class="link-code-hint">Send <code>/link ' + esc(code) + '</code> to bot</div>' +
    '</div>' +
    '<div class="link-code-actions">' +
      '<button class="btn-copy-code" id="btnCopyCode">' +
        '<i class="bi bi-clipboard"></i> Copy' +
      '</button>' +
      '<span class="link-code-timer" id="linkCodeTimer">10:00</span>' +
    '</div>';

  document.getElementById('btnCopyCode')?.addEventListener('click', () => {
    navigator.clipboard.writeText(code).then(() => {
      const btn = document.getElementById('btnCopyCode');
      if (btn) {
        btn.innerHTML = '<i class="bi bi-check2"></i> Copied';
        setTimeout(() => { btn.innerHTML = '<i class="bi bi-clipboard"></i> Copy'; }, 2000);
      }
    });
  });

  if (countdownTimer) clearInterval(countdownTimer);
  countdownTimer = setInterval(() => {
    const remaining = Math.max(0, expiryTime - Date.now());
    const mins = Math.floor(remaining / 60000);
    const secs = Math.floor((remaining % 60000) / 1000);
    const el = document.getElementById('linkCodeTimer');
    if (el) el.textContent = mins + ':' + String(secs).padStart(2, '0');
    if (remaining <= 0) {
      clearInterval(countdownTimer);
      countdownTimer = null;
      renderUnlinkedStatus();
    }
  }, 1000);
}

async function unlinkTelegram() {
  if (!confirm('Unlink your Telegram account?')) return;
  try {
    await axios.delete('/telegram/unlink');
    if (window.toast) window.toast.success('Telegram unlinked');
    renderUnlinkedStatus();
  } catch (e) {
    if (window.toast) window.toast.error(e.response?.data?.detail || 'Failed to unlink');
  }
}

function esc(s) {
  const d = document.createElement('div');
  d.textContent = s;
  return d.innerHTML;
}
